import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { MetricGrid, StatTile } from '@siliconx/shared/ui/MetricGrid';
import { EmptyState, ErrorState, LoadingShell } from '@siliconx/shared/ui/state';
import { formatUsd } from '../lib/format';
import { AlertCircle, ArrowRight, CheckCircle2, Package, ShieldAlert, HandCoins, Send } from 'lucide-react';
import { PortfolioUnits } from '../components/data/PortfolioUnits';
import type { ClientDashboardSummary,FinancialScenarioRow } from '@siliconx/shared';
import type { DataHealthResponse } from '@siliconx/shared/dto/data-health';
import type { DirectOffersResponse } from '../lib/types';
import { api, throwIfNotOk } from '../lib/api';
import { fetchClientIdentity } from '../lib/auth';
import { qk } from '../lib/queryKeys';
import { MarketPulse, MarketPulseRecommended } from '../components/dashboard/MarketPulse';
import { SerialEntryBanner } from '../components/SerialEntryNudge';
import { OrgFeature, useOrgFeature } from '../lib/orgFeatures';

export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
});

// ---------------------------------------------------------------------------
// API shapes (local — non-dashboard endpoints)
// ---------------------------------------------------------------------------

interface TransactionListItem {
  id: string;
  lifecycleState?: string;
}

interface TransactionListResponse {
  data: TransactionListItem[];
}

/**
 * SIL-716: named -Summary, not -Response, to avoid the false-duplicate
 * collision with opportunities.tsx's OpportunitiesResponse — same base
 * name, unrelated shape (this is a counts summary; that one is a
 * paginated list). Not merged.
 */
interface OpportunitiesSummaryResponse {
  /** Open offers this org's inventory could fill — standing demand. */
  openCount: number;
  /** Subset of openCount arriving since this user last opened /opportunities. */
  newCount: number;
  optedOut: boolean;
}

/** SIL-572: an open direct offer counts as "nearing expiry" inside this window. */
const EXPIRING_SOON_DAYS = 7;
/** ...and escalates to 'now' urgency inside this narrower window. */
const EXPIRING_URGENT_DAYS = 2;

// ---------------------------------------------------------------------------
// Work-queue model (SIL-546, W3 of resources/plans/wayfinder-ux-consistency-spec.md
// §3). Rule-generated from real signals only — see the SIL-530 prototype's
// DATA HONESTY note. "Unmatched ERP records" stays dropped — there is no
// queryable currently-unmatched count, only a per-run
// `erpMatchRunResponseSchema` result. Stale telemetry (data-health's `stale`
// anomaly) is the closest real analog and is what's rendered here.
//
// SIL-532 (I2-C2): "Scenarios to review" now ships as the 4th Overview
// counter (below), not a work-queue row — see WorkQueueOverview's own
// comment on why it's a separate MetricGrid tile rather than a 5th queue
// item.
//
// SIL-567 (amends SIL-546): the portfolio rail that used to sit beside this
// list is gone — Overview is full-width now. The rail's content (FMV total,
// asset counts, FMV-over-time) was never deleted from the product; it lives
// on Portfolio (`/assets` — per-class/location FMV + count subtotals) and
// Finance (`/financials/book-vs-fmv` — FMV trend). Overview keeps one link
// through rather than a duplicate summary. See
// `resources/plans/wayfinder-iter2-spec.md` §2.1 and `DESIGN.md`.
// ---------------------------------------------------------------------------

type Urgency = 'now' | 'soon' | 'whenever';
// SIL-572: 'buying' is the buyer-side counterpart to 'demand' (which is a
// seller reading buyer demand for its own inventory) — a buyer's own open
// direct offers lapsing unclaimed.
type Domain = 'transaction' | 'data' | 'demand' | 'buying';

interface WorkItem {
  id: string;
  title: string;
  /** One line of consequence — what happens if this stays ignored. */
  detail: string;
  cta: string;
  to: string;
  urgency: Urgency;
  domain: Domain;
}

const DOMAIN_ICON: Record<Domain, typeof HandCoins> = {
  transaction: ArrowRight,
  data: ShieldAlert,
  demand: HandCoins,
  buying: Send,
};

const URGENCY_DOT: Record<Urgency, string> = {
  now: 'bg-status-red',
  soon: 'bg-status-amber',
  whenever: 'bg-muted-foreground',
};

const URGENCY_RANK: Record<Urgency, number> = { now: 0, soon: 1, whenever: 2 };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function DashboardPage() {
  const identityQ = useQuery({
    queryKey: qk.identity(),
    queryFn: () => fetchClientIdentity(),
  });
  const identity = identityQ.data;
  // RUF-550: Overview shows the org's own work queue/portfolio, gated on
  // profile PRESENCE (hasSellerProfile/hasBuyerProfile), not completeness
  // (canSell/canBuy). An incomplete-but-present profile still sees it here.
  const isSeller = identity?.hasSellerProfile ?? false;
  const isBuyer = identity?.hasBuyerProfile ?? false;
  // SIL-1026/SIL-1034: the onboarding wizard is orgFeature: 'onboarding'-gated.
  const canOnboard = useOrgFeature('onboarding');
  // SIL-1036: buyer-demand (opportunities), data-health, and scenarios are
  // all `orgFeature`-gated business-only surfaces — an individual
  // customer_org has no fleet-data/scenario workflow and isn't matched
  // against buyer demand the way a seller's inventory is. Gating the
  // hook here (not just the tile below) is what keeps the fetch itself from
  // firing — these previously 403'd for an individual.
  const canMarketplaceOps = useOrgFeature('marketplaceOps');
  const canFleetData = useOrgFeature('fleetData');
  const canScenarios = useOrgFeature('scenarios');

  // Seller portfolio summary — ONE round-trip (replaces the old limit=200 +
  // per-asset N+1 snapshot waterfall).
  const summaryQ = useQuery<ClientDashboardSummary>({
    queryKey: qk.dashboard.summary(),
    queryFn: async () => {
      const res = await api.api.client.dashboard.summary.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as ClientDashboardSummary;
    },
    enabled: isSeller,
  });

  // Active transactions + which are awaiting the seller's own approval.
  const txQ = useQuery<TransactionListResponse>({
    queryKey: qk.dashboard.transactionsPending(),
    queryFn: async () => {
      const res = await api.api.client.transactions.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as TransactionListResponse;
    },
    enabled: isSeller,
  });

  // Open demand — direct offers this org's inventory could fill.
  // SIL-1036: orgFeature 'marketplaceOps' — an individual has no fleet
  // inventory to match against buyer demand.
  const oppsQ = useQuery<OpportunitiesSummaryResponse>({
    queryKey: qk.opportunities.badge(),
    queryFn: async () => {
      const res = await api.api.client.opportunities.$get({ query: {} });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as OpportunitiesSummaryResponse;
    },
    enabled: isSeller && canMarketplaceOps,
  });

  // Data health — zombie/ghost/stale anomaly counts.
  // SIL-1036: orgFeature 'fleetData' — no fleet integrations/data-health
  // surface for an individual.
  const dataHealthQ = useQuery<DataHealthResponse>({
    queryKey: qk.dataHealth(),
    queryFn: async () => {
      const res = await api.api.client['data-health'].$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as DataHealthResponse;
    },
    enabled: isSeller && canFleetData,
  });

  // SIL-532 (I2-C2) — scenarios, embedding the SAME staleness read model the
  // scenario-list badges and assumption-table row badges consume (see
  // `financial.ts`'s scenarioStalenessSchema). The 4th Overview counter
  // reduces this same array; no separate summary endpoint.
  // SIL-1036: orgFeature 'scenarios' — no what-if scenario workflow for an
  // individual.
  const scenariosQ = useQuery<FinancialScenarioRow[]>({
    queryKey: qk.scenarios.all,
    queryFn: async () => {
      const res = await api.api.client.scenarios.$get({ query: {} });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as FinancialScenarioRow[];
    },
    enabled: isSeller && canScenarios,
  });

  // SIL-572: the buyer's own open direct offers — sourced regardless of
  // isSeller so a dual-profile org's queue AND a buyer-only org's queue both
  // see it. GET /api/client/direct-offers already returns only the caller's
  // own rows (buyerCustomerOrgId scoped server-side), so no extra filtering
  // is needed beyond status/expiresAt.
  const directOffersQ = useQuery<DirectOffersResponse>({
    queryKey: qk.directOffers(),
    queryFn: async () => {
      const res = await api.api.client['direct-offers'].$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as DirectOffersResponse;
    },
    enabled: isBuyer,
  });

  const expiringOpenOffers = (() => {
    const now = Date.now();
    const rows = (directOffersQ.data?.data ?? []).filter(
      (d) => d.status === 'open' && d.expiresAt != null,
    );
    const withDaysLeft = rows.map((d) => ({
      ...d,
      daysLeft: (new Date(d.expiresAt as string).getTime() - now) / 86_400_000,
    }));
    const soon = withDaysLeft.filter((d) => d.daysLeft >= 0 && d.daysLeft <= EXPIRING_SOON_DAYS);
    return {
      count: soon.length,
      valueUsd: soon.reduce(
        (sum, d) => sum + (d.quantityDesired - d.quantityFilled) * d.pricePerUnitUsd,
        0,
      ),
      soonestDays: soon.length > 0 ? Math.min(...soon.map((d) => d.daysLeft)) : null,
    };
  })();

  const summary = summaryQ.data;
  const isEmptyPortfolio = isSeller && summary !== undefined && summary.totalAssetCount === 0;

  // SIL-1036: oppsQ/dataHealthQ are `enabled: false` for an org lacking the
  // gating feature, and a disabled TanStack Query stays `isPending: true`
  // forever (it never fetches) — folding them into `isPending` unconditionally
  // would leave the work queue skeleton spinning forever for that org.
  const workQueueLoading =
    txQ.isPending ||
    (canMarketplaceOps && oppsQ.isPending) ||
    (canFleetData && dataHealthQ.isPending);
  const workQueueError: unknown =
    txQ.error ?? (canMarketplaceOps ? oppsQ.error : null) ?? (canFleetData ? dataHealthQ.error : null);
  const hasWorkQueueError = workQueueError != null;

  // Buyer-only org (no seller profile) — its own loading/error state, scoped
  // to the one query it actually issues (directOffersQ; txQ/oppsQ/dataHealthQ
  // are all seller-gated and never resolve for this org).
  const buyerWorkQueueLoading = directOffersQ.isPending;
  const buyerWorkQueueError: unknown = directOffersQ.error;
  const hasBuyerWorkQueueError = buyerWorkQueueError != null;
  const buyerWorkItems = deriveWorkQueue({
    awaitingYou: 0,
    zombieCount: 0,
    staleCount: 0,
    ghostCount: 0,
    openDemand: 0,
    expiringOpenOfferCount: expiringOpenOffers.count,
    expiringOpenOfferValueUsd: expiringOpenOffers.valueUsd,
    soonestExpiryDays: expiringOpenOffers.soonestDays,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Overview"
        subtitle="View work that needs your attention."
        pinTo="/dashboard"
        actions={
          <>
            <Link
              to="/listings"
              className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
            >
              Sell assets
            </Link>
            <Link
              to="/marketplace"
              className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
            >
              Browse exchange
            </Link>
          </>
        }
      />

      {/* SIL-2141: Market Pulse - deepest discounts, exchange summary (recommended: see bottom). */}
      <MarketPulse />

      {/* 929: one-time manual serial entry nudge (individual orgs, TrueRead
          assets with no serial). Server-derived count; session-only dismissal. */}
      <SerialEntryBanner source={identity} />

      {/* Identity still resolving (or retrying a transient first-load failure) */}
      {identityQ.isLoading && <LoadingShell rows={3} />}

      {/* SIL-572: buyer-only org (no seller profile) — a real work queue when
          there's a sourced signal to show (its own open direct offers
          expiring soon), the prior plain-links landing otherwise. */}
      {isBuyer && !isSeller && (
        <BuyerWorkQueueCard
          directOffersQ={directOffersQ}
          items={buyerWorkItems}
          loading={buyerWorkQueueLoading}
          hasError={hasBuyerWorkQueueError}
          error={buyerWorkQueueError}
        />
      )}

      {/* Seller portfolio still loading/erroring */}
      {isSeller && summaryQ.isLoading && <LoadingShell rows={4} />}
      {isSeller && summaryQ.error && (
        <ErrorState
          title="Couldn't load portfolio."
          error={summaryQ.error}
          onRetry={() => void summaryQ.refetch()}
        />
      )}

      {/* SIL-519: assets owned but never FMV-priced contribute $0 to the FMV
          total below (v_client_portfolio_fmv_daily has no row for them until
          pricing is bootstrapped) — call that out so the total is never
          silently understated. */}
      {isSeller && summary && (
        <PortfolioUnits
          totalAssetCount={summary.totalAssetCount}
          totalLineCount={summary.totalLineCount}
        />
      )}

      {isSeller && summary && summary.unpricedAssetCount > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertCircle className="size-5 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">{summary.unpricedAssetCount.toLocaleString()}</span>{' '}
            asset{summary.unpricedAssetCount !== 1 ? 's' : ''} in your portfolio{' '}
            {summary.unpricedAssetCount !== 1 ? 'have' : 'has'} no FMV yet, excluded from the totals
            below.
          </p>
        </div>
      )}

      {isSeller && isEmptyPortfolio && (
        <div className="rounded-lg border border-zinc-200 bg-white p-10 text-center">
          <Package className="mx-auto mb-3 size-8 text-zinc-300" />
          <p className="text-sm font-medium text-zinc-700">No assets yet</p>
          <p className="mt-1 text-sm text-zinc-500">
            Once you{' '}
            <Link to="/assets" className="font-semibold text-brand-600 hover:underline">
              add assets
            </Link>
            , your work queue and portfolio trends will appear here.
          </p>
        </div>
      )}

      {isSeller && summary && !isEmptyPortfolio && (
        <WorkQueueOverview
          txQ={txQ}
          oppsQ={oppsQ}
          dataHealthQ={dataHealthQ}
          scenariosQ={scenariosQ}
          workQueueLoading={workQueueLoading}
          hasWorkQueueError={hasWorkQueueError}
          workQueueError={workQueueError}
          // SIL-572: dual-profile orgs (isSeller AND isBuyer) fold the buyer
          // work-queue row into this same "Current work" list rather than
          // showing a second card. Buyer-only orgs never reach this branch —
          // see BuyerWorkQueueCard above.
          expiringOpenOfferCount={isBuyer ? expiringOpenOffers.count : 0}
          expiringOpenOfferValueUsd={isBuyer ? expiringOpenOffers.valueUsd : 0}
          soonestExpiryDays={isBuyer ? expiringOpenOffers.soonestDays : null}
        />
      )}

      {/* SIL-2150: Recommended / New on the exchange sits at the bottom, above only the setup banner. */}
      <MarketPulseRecommended hasFleet={(summary?.totalAssetCount ?? 0) > 0} />

      {/* Optional onboarding entry point — orgs with a seller profile that
          haven't finished the wizard. Never forced; see __root.tsx. Copy
          switches once the org has imported assets: the remaining gap is the
          telemetry step, so we nudge them to "Complete your setup". This is
          org-lifecycle state (like SetupProgressChip in the topbar), not a
          work-queue item, so it stays a banner rather than a queue row.
          SIL-1026/SIL-1034: the wizard is orgFeature: 'onboarding'-gated
          (bulk CSV import — an individual org has no fleet to bulk-import),
          so this CTA is hidden for one; OrgFeatureGate would bounce them
          straight back here if they somehow reached /onboarding anyway. */}
      {identity?.canSell && !identity.onboardingCompleted && canOnboard && (
        <div className="flex items-center gap-3 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3">
          <Package className="size-5 shrink-0 text-brand-600" />
          <p className="flex-1 text-sm text-brand-700">
            {(summary?.totalAssetCount ?? 0) > 0
              ? 'Finish setting up telemetry to start getting FMV on your assets.'
              : 'Use the onboarding wizard to bulk-import your assets and get FMV faster.'}
          </p>
          <Link
            to="/onboarding"
            className="shrink-0 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
          >
            {(summary?.totalAssetCount ?? 0) > 0 ? 'Complete your setup' : 'Start onboarding'}
          </Link>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Work queue derivation — pure, so it's testable without mounting the query
// hooks (mirrors the SIL-530 prototype's `useWorkQueue` shape).
// ---------------------------------------------------------------------------

export function deriveWorkQueue({
  awaitingYou,
  zombieCount,
  staleCount,
  ghostCount,
  openDemand,
  // SIL-572: buyer-side row. Optional (default 0/null) so the seller-only
  // signals above stay byte-identical for callers that never pass these —
  // see dashboard-work-queue.test.ts.
  expiringOpenOfferCount = 0,
  expiringOpenOfferValueUsd = 0,
  soonestExpiryDays = null,
}: {
  awaitingYou: number;
  zombieCount: number;
  staleCount: number;
  ghostCount: number;
  openDemand: number;
  /** Count of the caller's OWN open direct offers expiring within EXPIRING_SOON_DAYS. */
  expiringOpenOfferCount?: number;
  /** Sum of (quantityDesired - quantityFilled) * pricePerUnitUsd across those offers. */
  expiringOpenOfferValueUsd?: number;
  /** Days until the soonest of those offers expires — null when none are expiring. */
  soonestExpiryDays?: number | null;
}): WorkItem[] {
  const items: WorkItem[] = [];

  if (awaitingYou > 0) {
    items.push({
      id: 'tx-awaiting',
      title:
        awaitingYou === 1 ? 'A transaction is waiting on you' : `${awaitingYou} transactions are waiting on you`,
      detail: 'Custody cannot transfer until you approve.',
      cta: 'Continue transaction',
      to: '/transactions',
      urgency: 'now',
      domain: 'transaction',
    });
  }

  if (zombieCount > 0) {
    items.push({
      id: 'dh-zombie',
      title:
        zombieCount === 1
          ? '1 asset carries book value but went dark'
          : `${zombieCount} assets carry book value but went dark`,
      detail: 'Still depreciating with no telemetry for 30+ days.',
      cta: 'Resolve data gaps',
      to: '/data-health',
      urgency: 'now',
      domain: 'data',
    });
  }

  if (openDemand > 0) {
    items.push({
      id: 'demand-new',
      title:
        openDemand === 1
          ? '1 open offer your inventory could fill'
          : `${openDemand} open offers your inventory could fill`,
      detail: 'Buyers are asking for hardware you already hold.',
      cta: 'View demand',
      to: '/opportunities',
      urgency: 'soon',
      domain: 'demand',
    });
  }

  if (ghostCount > 0) {
    items.push({
      id: 'dh-ghost',
      title:
        ghostCount === 1
          ? '1 asset is retired but still reporting telemetry'
          : `${ghostCount} assets are retired but still reporting telemetry`,
      detail: 'Lifecycle state disagrees with live data — one of them is wrong.',
      cta: 'Review assets',
      to: '/data-health',
      urgency: 'soon',
      domain: 'data',
    });
  }

  if (staleCount > 0) {
    items.push({
      id: 'dh-stale',
      title:
        staleCount === 1
          ? '1 asset has not reported telemetry in 30+ days'
          : `${staleCount} assets have not reported telemetry in 30+ days`,
      detail: 'Health scores and FMV confidence decay until data resumes.',
      cta: 'Review assets',
      to: '/data-health',
      urgency: 'whenever',
      domain: 'data',
    });
  }

  // SIL-572: a buyer's own open direct offer, unclaimed as expiresAt
  // approaches — direct_offer (653/670) has a real status ('open') and a
  // real expiresAt; the open value is the unfilled remainder
  // (quantityDesired - quantityFilled) * pricePerUnitUsd. This is the ONLY
  // buyer work-queue row shipped by SIL-572: the issue's other proposed row
  // ("accepted quote with no transaction started") does not exist as a real
  // signal — no table links an accepted proposal to a marketplace_transaction
  // as an intermediate state. Per the issue's own "omit the row type if
  // unsourceable" rule, that row is not implemented rather than stubbed.
  // (SIL-654: the quote_request table this comment originally cited for the
  // "no accepted state" fact was retired; sealed auctions + direct_offer are
  // the only private-price paths now, and the underlying fact is unchanged.)
  if (expiringOpenOfferCount > 0) {
    const urgent = soonestExpiryDays !== null && soonestExpiryDays <= EXPIRING_URGENT_DAYS;
    items.push({
      id: 'buy-direct-offer-expiring',
      title:
        expiringOpenOfferCount === 1
          ? '1 open offer is about to expire unclaimed'
          : `${expiringOpenOfferCount} open offers are about to expire unclaimed`,
      detail: `${formatUsd(expiringOpenOfferValueUsd)} in open demand lapses if no seller claims it first.`,
      cta: 'Review direct offers',
      to: '/direct-offers',
      urgency: urgent ? 'now' : 'soon',
      domain: 'buying',
    });
  }

  // Stable sort — groups by urgency (now, soon, whenever) while preserving
  // each group's push order above (e.g. tx-awaiting before dh-zombie, both
  // 'now'). Needed because expiringOpenOfferCount's urgency is data-driven,
  // not a fixed push position like every other row's.
  return items.sort((a, b) => URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency]);
}

// ---------------------------------------------------------------------------
// Overview layout — SIL-567 (amends SIL-546): counters full width, "Current
// work" full width. The portfolio rail that used to occupy a 320px second
// column is gone; its FMV total/counts/trend live on Portfolio (`/assets`)
// and Finance (`/financials/book-vs-fmv`) instead. One compact link in the
// card header takes the place of the rail.
// ---------------------------------------------------------------------------

function WorkQueueOverview({
  txQ,
  oppsQ,
  dataHealthQ,
  scenariosQ,
  workQueueLoading,
  hasWorkQueueError,
  workQueueError,
  expiringOpenOfferCount = 0,
  expiringOpenOfferValueUsd = 0,
  soonestExpiryDays = null,
}: {
  txQ: ReturnType<typeof useQuery<TransactionListResponse>>;
  oppsQ: ReturnType<typeof useQuery<OpportunitiesSummaryResponse>>;
  dataHealthQ: ReturnType<typeof useQuery<DataHealthResponse>>;
  scenariosQ: ReturnType<typeof useQuery<FinancialScenarioRow[]>>;
  workQueueLoading: boolean;
  hasWorkQueueError: boolean;
  workQueueError: unknown;
  /** SIL-572: dual-profile (isSeller AND isBuyer) buyer signal — see dashboard.tsx's DashboardPage. */
  expiringOpenOfferCount?: number;
  expiringOpenOfferValueUsd?: number;
  soonestExpiryDays?: number | null;
}) {
  const transactions = txQ.data?.data ?? [];
  const awaitingYou = transactions.filter((t) => t.lifecycleState === 'pending_seller_approval').length;
  const activeTransactions = transactions.filter(
    (t) => t.lifecycleState !== 'completed' && t.lifecycleState !== 'cancelled',
  ).length;

  // Standing demand, NOT the unread badge: newCount resets to 0 the moment
  // anyone in the org opens /opportunities, which made this tile read "0"
  // while live offers sat waiting. newCount only qualifies the tile's note.
  const openDemand = oppsQ.data?.openCount ?? 0;
  const newDemand = oppsQ.data?.newCount ?? 0;

  const dh = dataHealthQ.data?.counts;
  const zombieCount = dh?.zombie ?? 0;
  const ghostCount = dh?.ghost ?? 0;
  const staleCount = dh?.stale ?? 0;
  const dataIssues = dh?.total ?? 0;

  // SIL-532 (I2-C2) — "to review" = stale ∪ in_review, settled
  // 2026-08-09 (wayfinder-iter2-spec.md §2.2). Reduces the SAME
  // `scenariosQ` array the scenario-list badges consume (via each
  // scenario's already-embedded `staleness`/`status`) — no separate
  // summary endpoint, no re-derived comparison.
  const scenarios = scenariosQ.data ?? [];
  const staleScenarioCount = scenarios.filter((s) => s.staleness?.stale).length;
  const awaitingApprovalScenarioCount = scenarios.filter((s) => s.status === 'in_review').length;
  const toReviewScenarioCount = scenarios.filter(
    (s) => s.staleness?.stale || s.status === 'in_review',
  ).length;

  const items = deriveWorkQueue({
    awaitingYou,
    zombieCount,
    staleCount,
    ghostCount,
    openDemand,
    expiringOpenOfferCount,
    expiringOpenOfferValueUsd,
    soonestExpiryDays,
  });

  return (
    <div className="space-y-6">
      {/* Four counters (SIL-532, I2-C2, amends SIL-546): "Scenarios to
          review" is the 4th tile, wired to the real staleness read model —
          split-labeled like "Active transactions" (headline = the stale ∪
          in_review union, sub-label = the awaiting-approval subset), the
          pattern settled in wayfinder-iter2-spec.md §2.2. Links to the
          scenario list pre-filtered to the same union (`?review=1`). */}
      <MetricGrid>
        <OrgFeature name="marketplaceOps">
          <Link to="/opportunities">
            <StatTile
              label="Market demand"
              value={openDemand.toLocaleString()}
              tone="good"
              note={
                openDemand === 0
                  ? 'no open offers'
                  : newDemand > 0
                    ? `${newDemand.toLocaleString()} new since your last visit`
                    : openDemand === 1
                      ? '1 offer your inventory could fill'
                      : `${openDemand.toLocaleString()} offers your inventory could fill`
              }
            />
          </Link>
        </OrgFeature>
        <Link to="/transactions">
          <StatTile
            label="Active transactions"
            value={activeTransactions.toLocaleString()}
            tone="blue"
            note={
              awaitingYou > 0
                ? `${awaitingYou.toLocaleString()} awaiting you`
                : 'none awaiting you'
            }
          />
        </Link>
        <OrgFeature name="fleetData">
          <Link to="/data-health">
            <StatTile
              label="Data issues"
              value={dataIssues.toLocaleString()}
              tone={dataIssues > 0 ? 'warn' : 'neutral'}
              note={`${zombieCount} zombie · ${ghostCount} ghost · ${staleCount} stale`}
            />
          </Link>
        </OrgFeature>
        <OrgFeature name="scenarios">
          <Link to="/financials/scenarios" search={{ review: true }}>
            <StatTile
              label="Scenarios to review"
              value={toReviewScenarioCount.toLocaleString()}
              tone={toReviewScenarioCount > 0 ? 'warn' : 'neutral'}
              note={
                awaitingApprovalScenarioCount > 0
                  ? `${awaitingApprovalScenarioCount.toLocaleString()} awaiting approval`
                  : toReviewScenarioCount > 0
                    ? `${staleScenarioCount.toLocaleString()} stale`
                    : 'none awaiting review'
              }
            />
          </Link>
        </OrgFeature>
      </MetricGrid>

      {/* Current work — full width (SIL-567 amends SIL-546: the 320px
          portfolio rail this used to share the row with is gone). The
          compact "Portfolio →" link in the header is the one link through
          to where the rail's FMV total/counts/trend actually live now. */}
      <Card padding="lg">
        <div className="flex items-baseline justify-between">
          <div>
            <SectionLabel>Needs attention</SectionLabel>
            <h2 className="mt-1 text-[15px] font-semibold text-foreground">Current work</h2>
          </div>
          <div className="flex items-center gap-3">
            {!workQueueLoading && !hasWorkQueueError && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {items.length} item{items.length !== 1 ? 's' : ''}
              </span>
            )}
            <Link to="/assets" className="text-sm font-medium text-brand-600 hover:underline">
              Portfolio →
            </Link>
          </div>
        </div>

        {workQueueLoading && <LoadingShell rows={4} />}
        {!workQueueLoading && hasWorkQueueError && (
          <ErrorState
            title="Couldn't load your work queue."
            error={workQueueError}
            onRetry={() => {
              void txQ.refetch();
              void oppsQ.refetch();
              void dataHealthQ.refetch();
            }}
          />
        )}
        {!workQueueLoading && !hasWorkQueueError && items.length === 0 && (
          <EmptyState
            icon={CheckCircle2}
            title="Nothing needs your attention"
            description="Every asset is reporting, no offers are open, and nothing is awaiting your approval."
            action={
              <Link to="/assets" className="text-sm font-medium text-brand-600 hover:underline">
                Review your portfolio
              </Link>
            }
          />
        )}
        {!workQueueLoading && !hasWorkQueueError && items.length > 0 && <WorkQueueRows items={items} />}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared row list — factored out of WorkQueueOverview (SIL-572) so
// BuyerWorkQueueCard (buyer-only orgs, no seller profile) can render the
// same row anatomy without duplicating the markup.
// ---------------------------------------------------------------------------

function WorkQueueRows({ items }: { items: WorkItem[] }) {
  return (
    <ul className="mt-4 divide-y divide-border">
      {items.map((it) => {
        const Icon = DOMAIN_ICON[it.domain];
        return (
          <li key={it.id} className="flex items-center gap-4 py-4">
            <span className={`size-1.5 shrink-0 rounded-full ${URGENCY_DOT[it.urgency]}`} />
            <Icon className="size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{it.title}</p>
              <p className="mt-0.5 text-sm text-muted-foreground">{it.detail}</p>
            </div>
            <Link to={it.to} className="shrink-0 text-sm font-medium text-brand-600 hover:underline">
              {it.cta} →
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Buyer-only org (no seller profile) work queue — SIL-572. Falls back to the
// pre-existing plain-links landing when there's nothing sourced to show
// (deriveWorkQueue currently ships exactly one buyer row — see its own
// comment on the "accepted quote" omission).
// ---------------------------------------------------------------------------

function BuyerWorkQueueCard({
  directOffersQ,
  items,
  loading,
  hasError,
  error,
}: {
  directOffersQ: ReturnType<typeof useQuery<DirectOffersResponse>>;
  items: WorkItem[];
  loading: boolean;
  hasError: boolean;
  error: unknown;
}) {
  if (loading) return <LoadingShell rows={3} />;

  if (hasError) {
    return (
      <ErrorState
        title="Couldn't load your work queue."
        error={error}
        onRetry={() => void directOffersQ.refetch()}
      />
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-8 text-center">
        <p className="text-sm text-zinc-500">
          View{' '}
          <Link to="/listings" className="font-semibold text-brand-600 hover:underline">
            listings
          </Link>
          , or browse{' '}
          <Link to="/inventory" className="font-semibold text-brand-600 hover:underline">
            inventory
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <Card padding="lg">
      <div className="flex items-baseline justify-between">
        <div>
          <SectionLabel>Needs attention</SectionLabel>
          <h2 className="mt-1 text-[15px] font-semibold text-foreground">Current work</h2>
        </div>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {items.length} item{items.length !== 1 ? 's' : ''}
        </span>
      </div>
      <WorkQueueRows items={items} />
    </Card>
  );
}
